/**
 * Section-based color theme for the dashboard.
 * Each nav section gets its own accent color to break the "all blue" monotony.
 */

export type SectionColor = 'blue' | 'violet' | 'emerald' | 'amber' | 'rose'

export interface ThemeColors {
  /** Primary button bg: bg-{color}-500 */
  btn: string
  /** Primary button hover: hover:bg-{color}-600 */
  btnHover: string
  /** Light bg for badges/selected states: bg-{color}-50 */
  bgLight: string
  /** Medium bg for badges: bg-{color}-100 */
  bgMedium: string
  /** Text on light bg: text-{color}-700 */
  text: string
  /** Icon/accent text: text-{color}-600 */
  accent: string
  /** Darker text: text-{color}-800 */
  textDark: string
  /** Focus ring: focus:ring-{color}-500 */
  ring: string
  /** Border for selected: border-{color}-500 */
  border: string
  /** Light border: border-{color}-200 */
  borderLight: string
  /** Hover bg: hover:bg-{color}-50 */
  hoverBg: string
  /** Spinner color: text-{color}-600 */
  spinner: string
  /** Active nav bg */
  navActive: string
  /** Active nav text */
  navText: string
  /** Active nav icon */
  navIcon: string
}

const colors: Record<SectionColor, ThemeColors> = {
  blue: {
    btn: 'bg-blue-500',
    btnHover: 'hover:bg-blue-600',
    bgLight: 'bg-blue-50',
    bgMedium: 'bg-blue-100',
    text: 'text-blue-700',
    accent: 'text-blue-600',
    textDark: 'text-blue-800',
    ring: 'focus:ring-blue-500',
    border: 'border-blue-500',
    borderLight: 'border-blue-200',
    hoverBg: 'hover:bg-blue-50',
    spinner: 'text-blue-600',
    navActive: 'bg-blue-100',
    navText: 'text-blue-700',
    navIcon: 'text-blue-600',
  },
  violet: {
    btn: 'bg-violet-500',
    btnHover: 'hover:bg-violet-600',
    bgLight: 'bg-violet-50',
    bgMedium: 'bg-violet-100',
    text: 'text-violet-700',
    accent: 'text-violet-600',
    textDark: 'text-violet-800',
    ring: 'focus:ring-violet-500',
    border: 'border-violet-500',
    borderLight: 'border-violet-200',
    hoverBg: 'hover:bg-violet-50',
    spinner: 'text-violet-600',
    navActive: 'bg-violet-100',
    navText: 'text-violet-700',
    navIcon: 'text-violet-600',
  },
  emerald: {
    btn: 'bg-emerald-500',
    btnHover: 'hover:bg-emerald-600',
    bgLight: 'bg-emerald-50',
    bgMedium: 'bg-emerald-100',
    text: 'text-emerald-700',
    accent: 'text-emerald-600',
    textDark: 'text-emerald-800',
    ring: 'focus:ring-emerald-500',
    border: 'border-emerald-500',
    borderLight: 'border-emerald-200',
    hoverBg: 'hover:bg-emerald-50',
    spinner: 'text-emerald-600',
    navActive: 'bg-emerald-100',
    navText: 'text-emerald-700',
    navIcon: 'text-emerald-600',
  },
  amber: {
    btn: 'bg-amber-500',
    btnHover: 'hover:bg-amber-600',
    bgLight: 'bg-amber-50',
    bgMedium: 'bg-amber-100',
    text: 'text-amber-700',
    accent: 'text-amber-600',
    textDark: 'text-amber-800',
    ring: 'focus:ring-amber-500',
    border: 'border-amber-500',
    borderLight: 'border-amber-200',
    hoverBg: 'hover:bg-amber-50',
    spinner: 'text-amber-600',
    navActive: 'bg-amber-100',
    navText: 'text-amber-700',
    navIcon: 'text-amber-600',
  },
  rose: {
    btn: 'bg-rose-500',
    btnHover: 'hover:bg-rose-600',
    bgLight: 'bg-rose-50',
    bgMedium: 'bg-rose-100',
    text: 'text-rose-700',
    accent: 'text-rose-600',
    textDark: 'text-rose-800',
    ring: 'focus:ring-rose-500',
    border: 'border-rose-500',
    borderLight: 'border-rose-200',
    hoverBg: 'hover:bg-rose-50',
    spinner: 'text-rose-600',
    navActive: 'bg-rose-100',
    navText: 'text-rose-700',
    navIcon: 'text-rose-600',
  },
}

/** Map route prefixes to section colors */
const routeColorMap: Array<[string, SectionColor]> = [
  // BUILD section — violet
  ['/agents', 'violet'],
  ['/skills', 'violet'],
  ['/training', 'violet'],
  ['/cowork', 'violet'],
  // KNOWLEDGE section — emerald
  ['/knowledge', 'emerald'],
  ['/documents', 'emerald'],
  ['/knowledge-map', 'emerald'],
  // DEPLOY section — amber
  ['/channels', 'amber'],
  ['/integrations', 'amber'],
  // SOCIAL section — rose
  ['/social-capture', 'rose'],
  // MONITOR section — rose
  ['/conversations', 'rose'],
  ['/contacts', 'rose'],
  ['/bots', 'rose'],
  ['/simulations', 'rose'],
  ['/usage', 'rose'],
]

export function getThemeColors(section: SectionColor): ThemeColors {
  return colors[section]
}

export function getSectionForRoute(pathname: string): SectionColor {
  for (const [prefix, color] of routeColorMap) {
    if (pathname.startsWith(prefix)) return color
  }
  return 'blue' // default for dashboard, billing, settings, etc.
}

export function getColorsForRoute(pathname: string): ThemeColors {
  return colors[getSectionForRoute(pathname)]
}

export { colors as themeColors }
